import { z } from "zod";

/** Notifications (WP-3.4, ADR-0023): what happened to a request, for one person. */

export const NOTIFICATION_KINDS = [
  /** To the requester: their request was submitted (confirmation). */
  "REQUEST_SUBMITTED",
  /** To the buyers who could work it: a new request was submitted. */
  "NEW_REQUEST",
  /** To a buyer a manager assigned it to. */
  "REQUEST_ASSIGNED",
  /** To the requester: procurement returned it with questions. */
  "QUESTIONS_FROM_PROCUREMENT",
  /** To the assigned buyer: the requester answered and submitted it again. */
  "REQUEST_RESUBMITTED",
  "REQUEST_APPROVED",
  "REQUEST_REJECTED",
  // Stalled requests (WP-3.5, ADR-0024).
  /** To the requester: their draft has had no activity. */
  "DRAFT_REMINDER",
  /** To the requester: last reminder before the draft is closed. */
  "DRAFT_LAST_REMINDER",
  /** To the requester: the draft was closed for inactivity (it can be reopened). */
  "REQUEST_ABANDONED",
  /** To the requester: procurement's questions are still unanswered. */
  "ANSWER_REMINDER",
  /** To the assigned buyer: the requester has not answered despite a reminder. */
  "ANSWERS_OVERDUE",
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

/** Kinds addressed to the requester (they open the requester's view); the rest open the review page. */
export const REQUESTER_NOTIFICATIONS: ReadonlySet<NotificationKind> = new Set([
  "REQUEST_SUBMITTED",
  "QUESTIONS_FROM_PROCUREMENT",
  "REQUEST_APPROVED",
  "REQUEST_REJECTED",
  "DRAFT_REMINDER",
  "DRAFT_LAST_REMINDER",
  "REQUEST_ABANDONED",
  "ANSWER_REMINDER",
]);

/** The app path a notification opens. */
export function notificationPath(kind: NotificationKind, requestId: string): string {
  return REQUESTER_NOTIFICATIONS.has(kind) ? `/requests/${requestId}` : `/queue/${requestId}`;
}

export const notificationViewSchema = z.object({
  id: z.uuid(),
  kind: z.enum(NOTIFICATION_KINDS),
  request: z.object({ id: z.uuid(), reference: z.string(), title: z.string().nullable() }),
  /** Who caused it (null for the system). */
  actor: z.string().nullable(),
  createdAt: z.string(),
  readAt: z.string().nullable(),
});
export type NotificationView = z.infer<typeof notificationViewSchema>;

export const notificationListSchema = z.object({
  items: z.array(notificationViewSchema),
  unread: z.number().int(),
});
export type NotificationList = z.infer<typeof notificationListSchema>;

/** How many notifications the panel lists (newest first). */
export const NOTIFICATIONS_SHOWN = 20;
